import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';

import { BasicContentService } from './basic-content-service';
import { GlobalTablesService } from './global-tables-service';
import { LanguageSettings } from './language-settings';
import { NomWebServicesJson } from './nom-web-services-json';

@Injectable({ providedIn: 'root' })
export class PublicationChannelsService {
    constructor(
        private nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public globalTablesService: GlobalTablesService,
        public basicContentService: BasicContentService
    ) {}

    public async createPublicationChannelRecord(ch: StorytellingPublicationChannelRecord): Promise<ServiceResponse<StorytellingPublicationChannelRecord>> {
        ch.Owner = this.nomWebServicesJson.UserId;
        const database = this.nomWebServicesJson.getDatabase();
        const path = this.nomWebServicesJson.getPath(true);
        const data = this.nomWebServicesJson.convertor.serialize(ch);
        delete data.guid;
        delete data.Guid;
        return this.basicContentService.createBasicContent<StorytellingPublicationChannelRecord>(database, path, false, data);
    }

    public async updatePublicationChannelRecord(ch: StorytellingPublicationChannelRecord): Promise<ServiceResponse<StorytellingPublicationChannelRecord>> {
        ch.Owner = this.nomWebServicesJson.UserId;
        const database = this.nomWebServicesJson.getDatabase();
        const path = this.nomWebServicesJson.getPath(true);
        const { Guid } = ch;
        ch.PSTPublicationChannelEditionNames = ch.PSTPublicationChannelEditionNames?.split('|')
            .map(el => el.trim())
            .filter(el => !!el)
            .join('|');
        // Title is missing in this.globalTablesService.FieldDefsDict so it is not updated - ask Yanko
        const data = this.nomWebServicesJson.convertor.serialize(ch, true);
        return this.basicContentService.updateBasicContentV2<StorytellingPublicationChannelRecord>(database, path, Guid, false, data, false, null);
    }

    public async getPublicationChannelRecord(guid: string): Promise<ServiceResponse<StorytellingPublicationChannelRecord>> {
        const zFields: Array<{ FieldName: string; FieldValue: any }> = [
            { FieldName: 'guid', FieldValue: guid },
            { FieldName: 'type', FieldValue: ObjectTypes.PUBLICATION_CHANNEL },
        ];

        const params = this.getBaseParams();
        params.zFields = zFields;
        params.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/Search', params, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load publication channels!',
                detailMessage: `Failed to load publication channels! \n\rStackTrace: PublicationChannelsService.getPublicationChannelRecords(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingPublicationChannelRecord>(false, null);
        }

        if (response) {
            const responseJSON = response.body;
            if (responseJSON.SearchResults && responseJSON.SearchResults.length > 0) {
                const serviceRecord: ServiceRecord = ServiceRecord.instance(responseJSON.SearchResults[0]);
                const channelRecord: StorytellingPublicationChannelRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingPublicationChannelRecord>(serviceRecord);
                // DataStore.set(channelRecord);
                return new ServiceResponse<StorytellingPublicationChannelRecord>(true, channelRecord);
            }
        }
        return new ServiceResponse<StorytellingPublicationChannelRecord>(false, null);
    }

    public async getPublicationChannelRecords(): Promise<ServiceResponse<StorytellingPublicationChannelRecord[]>> {
        const results: StorytellingPublicationChannelRecord[] = [];

        const zFields: Array<{ FieldName: string; FieldValue: any }> = [
            { FieldName: 'virtualPath', FieldValue: `\\\\${this.nomWebServicesJson.PSTServer}` },
            { FieldName: 'type', FieldValue: ObjectTypes.PUBLICATION_CHANNEL },
        ];

        const params = this.getBaseParams();
        params.zFields = zFields;

        let response: HttpResponse<any> = null;
        try {
            response = await this.nomWebServicesJson.post('/api/Search', params, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load publication channels!',
                detailMessage: `Failed to load publication channels! \n\rStackTrace: PublicationChannelsService.getPublicationChannelRecords(); \n\rError: ${error}`,
            });
            return new ServiceResponse<StorytellingPublicationChannelRecord[]>(true, results);
        }

        if (response?.body?.SearchResults) {
            for (const p of response.body.SearchResults) {
                const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                try {
                    const channelRecord: StorytellingPublicationChannelRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingPublicationChannelRecord>(serviceRecord);
                    // DataStore.set(channelRecord);
                    // results.add(channelRecord);
                    results.push(channelRecord);
                } catch (e) {
                    console.log(serviceRecord);
                    console.log(e);
                }
            }
        }
        const res = new ServiceResponse<StorytellingPublicationChannelRecord[]>(true, results);
        // if (res.status) {
        //     DataStore.publicationChannelsResults = res.data;
        // }
        return res;
    }

    protected getBaseParams(): any {
        const params = {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: '',
            zFields: null,
            NOMLocations: [
                {
                    BasePath: `P_${this.nomWebServicesJson.PSTGroup}\\`,
                    SearchWIP: true,
                    SearchArchives: false,
                    SearchTrash: false,
                },
            ],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            MaxRows: 300,
            SortByRank: false,
            ExactMatch: false,
            FieldsToExclude: ['backfields'],
            FieldsToReturn: null,
        };

        return params;
    }
}
